import { createSelector } from 'reselect';
import {
    get,
    isEqual
} from 'lodash';
import { getEntityByName } from '../appEntities';
import {
    CHARGEBACK_FAILURE,
    CHARGEBACK_SUCCESS,
    CHARGEBACK_LOAD,
    DISPUTE_CHARGEBACK_LOAD,
    DISPUTE_CHARGEBACK_FAILURE,
    DISPUTE_CHARGEBACK_SUCCESS,
    DISPUTE_FAILURE,
    DISPUTE_LOAD,
    DISPUTE_SUCCESS,
    DISPUTES_LOAD,
    DISPUTES_SUCCESS,
    DISPUTES_FAILURE,
    DISPUTE_PDF_LOAD,
    DISPUTE_PDF_SUCCESS,
    DISPUTE_PDF_FAILURE,
    PREVIEW_PDF_FAILURE,
    PREVIEW_PDF_LOAD,
    PREVIEW_PDF_SUCCESS
} from './chargebackActions';

const initialState = {
    chargeback: null,
    error: null,
    loading: false
};

export default function reducer(state = initialState, action) {
    let newState;

    switch (action.type) {
        case CHARGEBACK_FAILURE:
            newState = {
                ...state,
                error: action.error,
                loading: false
            };

            return isEqual(state, newState) ? state : newState;

        case CHARGEBACK_LOAD:
            newState = {
                ...state,
                chargeback: null,
                disputes: [],
                loading: true
            };

            return isEqual(state, newState) ? state : newState;

        case CHARGEBACK_SUCCESS:
            newState = {
                ...state,
                chargeback: action.chargeback,
                loading: false
            };

            return isEqual(state, newState) ? state : newState;

        case DISPUTE_CHARGEBACK_FAILURE:
            newState = {
                ...state,
                disputeChargebackError: action.error,
                disputeChargebackLoading: false
            };

            return isEqual(state, newState) ? state : newState;

        case DISPUTE_CHARGEBACK_LOAD:
            newState = {
                ...state,
                disputeChargebackError: null,
                disputeChargebackLoading: true,
                disputeChargebackSuccessful: false
            };

            return isEqual(state, newState) ? state : newState;

        case DISPUTE_CHARGEBACK_SUCCESS:
            newState = {
                ...state,
                fax: action.fax,
                disputeChargebackLoading: false,
                disputeChargebackSuccessful: true
            };

            return isEqual(state, newState) ? state : newState;

        case DISPUTE_FAILURE:
            newState = {
                ...state,
                disputeError: action.error,
                disputeLoading: false
            };

            return isEqual(state, newState) ? state : newState;

        case DISPUTE_LOAD:
            newState = {
                ...state,
                disputeLoading: true
            };

            return isEqual(state, newState) ? state : newState;

        case DISPUTE_SUCCESS:
            newState = {
                ...state,
                dispute: action.dispute,
                disputeLoading: false
            };

            return isEqual(state, newState) ? state : newState;

        case DISPUTES_LOAD:
            newState = {
                ...state,
                count: 0,
                disputes: [],
                disputesError: null,
                disputesLoading: true,
                pages: 0
            };

            return isEqual(state, newState) ? state : newState;

        case DISPUTES_SUCCESS:
            newState = {
                ...state,
                count: action.count,
                disputes: action.disputes,
                disputesLoading: false,
                pages: action.pages
            };

            return isEqual(state, newState) ? state : newState;

        case DISPUTES_FAILURE:
            newState = {
                ...state,
                disputesError: action.error,
                disputesLoading: false
            };

            return isEqual(state, newState) ? state : newState;

        case DISPUTE_PDF_LOAD:
            newState = {
                ...state,
                disputePdfs: {
                    ...state.disputePdfs,
                    [action.chargebackId]: {
                        pdfData: null,
                        disputePdfError: null,
                        disputePdfLoading: true
                    }
                }
            };

            return isEqual(state, newState) ? state : newState;

        case DISPUTE_PDF_SUCCESS:
            newState = {
                ...state,
                disputePdfs: {
                    ...state.disputePdfs,
                    [action.chargebackId]: {
                        pdfData: action.disputePdf,
                        disputePdfLoading: false
                    }
                }
            };

            return isEqual(state, newState) ? state : newState;

        case DISPUTE_PDF_FAILURE:
            newState = {
                ...state,
                disputePdfs: {
                    ...state.disputePdfs,
                    [action.chargebackId]: {
                        disputePdfError: action.error,
                        disputePdfLoading: false
                    }
                }
            };

            return isEqual(state, newState) ? state : newState;

        case PREVIEW_PDF_LOAD:
            newState = {
                ...state,
                previewPdfs: {
                    ...state.previewPdfs,
                    [action.chargebackId]: {
                        pdfData: null,
                        previewPdfError: null,
                        previewPdfLoading: true
                    }
                }
            };

            return isEqual(state, newState) ? state : newState;

        case PREVIEW_PDF_SUCCESS:
            newState = {
                ...state,
                previewPdfs: {
                    ...state.previewPdfs,
                    [action.chargebackId]: {
                        pdfData: action.previewPdf,
                        previewPdfLoading: false
                    }
                }
            };

            return isEqual(state, newState) ? state : newState;

        case PREVIEW_PDF_FAILURE:
            newState = {
                ...state,
                previewPdfs: {
                    ...state.previewPdfs,
                    [action.chargebackId]: {
                        previewPdfError: action.error,
                        previewPdfLoading: false
                    }
                }
            };

            return isEqual(state, newState) ? state : newState;

        default:
            return state;
    }
}

export const getChargebackEntity = createSelector(
    (state) => state,
    (state) => getEntityByName(state, 'chargeback')
);

export const getIsChargebackLoading = createSelector(
    getChargebackEntity,
    (entity) => get(entity, 'loading')
);

export const getChargebackError = createSelector(
    getChargebackEntity,
    (entity) => get(entity, 'error')
);

export const getChargebackErrorMessage = createSelector(
    getChargebackError,
    (entity) => get(entity, 'message')
);

export const getChargeback = createSelector(
    getChargebackEntity,
    (entity) => get(entity, 'chargeback')
);

export const getIsChargebackDisputeLoading = createSelector(
    getChargebackEntity,
    (entity) => get(entity, 'disputeChargebackLoading')
);

export const getIsChargebackDisputeSuccessful = createSelector(
    getChargebackEntity,
    (entity) => get(entity, 'disputeChargebackSuccessful')
);

export const getChargebackDisputeFax = createSelector(
    getChargebackEntity,
    (entity) => get(entity, 'fax')
);

export const getChargebackDisputeError = createSelector(
    getChargebackEntity,
    (entity) => get(entity, 'disputeChargebackError')
);

export const getChargebackDisputeErrorMessage = createSelector(
    getChargebackDisputeError,
    (entity) => get(entity, 'message')
);

export const getDisputeError = createSelector(
    getChargebackEntity,
    (entity) => get(entity, 'disputeError')
);

export const getDisputeErrorMessage = createSelector(
    getDisputeError,
    (entity) => get(entity, 'message')
);

export const getDisputesError = createSelector(
    getChargebackEntity,
    (entity) => get(entity, 'disputesError')
);

export const getDisputesErrorMessage = createSelector(
    getDisputesError,
    (entity) => get(entity, 'message')
);

export const getIsDisputeLoading = createSelector(
    getChargebackEntity,
    (entity) => get(entity, 'disputeLoading')
);

export const getIsDisputesLoading = createSelector(
    getChargebackEntity,
    (entity) => get(entity, 'disputesLoading')
);

export const getPages = createSelector(
    getChargebackEntity,
    (entity) => get(entity, 'pages')
);

export const getCount = createSelector(
    getChargebackEntity,
    (entity) => get(entity, 'count')
);

export const getDisputes = createSelector(
    getChargebackEntity,
    (entity) => get(entity, 'disputes')
);

export const getDispute = createSelector(
    getChargebackEntity,
    (entity) => get(entity, 'dispute')
);

export const getDisputePdfs = createSelector(
    getChargebackEntity,
    (entity) => get(entity, 'disputePdfs')
);

export const getDisputePdfObject = createSelector(
    getDisputePdfs,
    (state, chargebackId) => chargebackId,
    (disputePdf, chargebackId = '') => get(disputePdf, chargebackId)
);

export const getIsDisputePdfLoading = createSelector(
    (state, chargebackId) => getDisputePdfObject(state, chargebackId),
    (disputePdfObject) => get(disputePdfObject, 'disputePdfLoading')
);

export const getDisputePdfError = createSelector(
    (state, chargebackId) => getDisputePdfObject(state, chargebackId),
    (disputePdfObject) => get(disputePdfObject, 'disputePdfError')
);

export const getDisputePdfErrorMessage = createSelector(
    (state, chargebackId) => getDisputePdfError(state, chargebackId),
    (error) => get(error, 'message')
);

export const getDisputePdfData = createSelector(
    (state, chargebackId) => getDisputePdfObject(state, chargebackId),
    (disputePdfObject) => get(disputePdfObject, 'pdfData')
);

export const getPreviewPdfs = createSelector(
    getChargebackEntity,
    (entity) => get(entity, 'previewPdfs')
);

export const getPreviewPdfObject = createSelector(
    getPreviewPdfs,
    (state, chargebackId) => chargebackId,
    (previewPdf, chargebackId = '') => get(previewPdf, chargebackId)
);

export const getIsPreviewPdfLoading = createSelector(
    (state, chargebackId) => getPreviewPdfObject(state, chargebackId),
    (previewPdfObject) => get(previewPdfObject, 'previewPdfLoading')
);

export const getPreviewPdfError = createSelector(
    (state, chargebackId) => getPreviewPdfObject(state, chargebackId),
    (previewPdfObject) => get(previewPdfObject, 'previewPdfError')
);

export const getPreviewPdfErrorMessage = createSelector(
    (state, chargebackId) => getPreviewPdfError(state, chargebackId),
    (error) => get(error, 'message')
);

export const getPreviewPdfData = createSelector(
    (state, chargebackId) => getPreviewPdfObject(state, chargebackId),
    (previewPdfObject) => get(previewPdfObject, 'pdfData')
);

